import { isAuthenticated } from '../auth-manager';
import {
  getSplashConfig,
  ISplashConfig,
  IEmployerContent,
  getEmployerContentSafely,
  loadKenticoData,
  isSplashBypassed,
} from '../remote-config-manager';
import { enums } from '..';
import {
  getPersonData,
  getEmployerPreferences,
  getPreferences,
} from '../web-apis-client';
import {
  IKenticoData,
  refreshCallCenter,
} from '../../contexts/kentico-data-context';
import { handleRefresh, startRefresh } from '../../hooks/use-refresh-handler';
import {
  FullRefreshStatuses,
  LoginRefreshStatuses,
} from '../../contexts/refresh-context';
import { log } from '../log';
import { initializeCommonChat } from '../amazon-connect-chat';

/*******************************************************************************
 * startup-manager.js is a collection of functions to assist with the startup
 * routine of the app (splash screen config, authentication, remote config, etc)
 ******************************************************************************/

export interface StartUpSequenceResult {
  state: string;
  context: {
    kentico: IKenticoData;
    remoteConfig?: {};
    splashConfig?: ISplashConfig;
    handleLoginResult?: boolean;
    personData?: { [name: string]: any };
    featureFlags?: {};
    employerPreferences?: {};
    refreshResult: {
      status: string;
      loginRefreshStatus: { status: LoginRefreshStatuses };
      fullRefreshStatus: { status: FullRefreshStatuses };
      statusDetail: any;
    };
    ERContent?: IEmployerContent;
    usingCachedData?: boolean;
  };
  error?: unknown;
}

const checkSplashConfig = async (returnObj: StartUpSequenceResult) => {
  try {
    // get splash config first
    returnObj.state = enums.AppStates.GET_SPLASH_CONFIG;
    const splashConfig = await getSplashConfig();
    returnObj.context.splashConfig = splashConfig;
  } catch (error) {
    returnObj.state = enums.AppStates.GET_SPLASH_CONFIG_ERROR;
    returnObj.error = error;
    throw error;
  }
};

const getKenticoData = async (returnObj: StartUpSequenceResult) => {
  try {
    returnObj.state = enums.AppStates.GET_KENTICO_DATA;
    const data = await loadKenticoData();
    returnObj.context.kentico.assets = data.assets;
    returnObj.context.kentico.featureFlags = data.featureFlags;
    returnObj.context.kentico.config = data.config;
    returnObj.context.kentico.localizedStrings = data.localizedStrings;
    returnObj.context.kentico.dynamicContent = data.dynamicContent;
  } catch (error) {
    returnObj.state = enums.AppStates.GET_KENTICO_DATA_ERROR;
    returnObj.error = error;
    throw error;
  }
};

const tryAuthenticating = async (returnObj: StartUpSequenceResult) => {
  try {
    returnObj.state = enums.AppStates.AUTHENTICATING;
    let loggedIn = false;
    loggedIn = await isAuthenticated();
    returnObj.context.handleLoginResult = loggedIn;
  } catch (error) {
    returnObj.state = enums.AppStates.AUTHENTICATION_ERROR;
    returnObj.error = error;
    throw error;
  }
};

const loginRefresh = async (returnObj: StartUpSequenceResult) => {
  if (sessionStorage.getItem(enums.CacheKeys.login_refresh_bypass) === 'true') {
    returnObj.context.refreshResult.loginRefreshStatus.status =
      enums.RefreshStatus.refreshed;
    sessionStorage.removeItem(enums.CacheKeys.login_refresh_bypass);
  } else {
    returnObj.context.refreshResult = await handleRefresh(true, false);
  }

  //trigger the full refresh
  if (sessionStorage.getItem(enums.CacheKeys.full_refresh_bypass) === 'true') {
    returnObj.context.refreshResult.fullRefreshStatus.status =
      enums.RefreshStatus.refreshed;
    sessionStorage.removeItem(enums.CacheKeys.full_refresh_bypass);
  } else {
    await startRefresh(false, true);
  }
};

const checkRefreshStatus = async (returnObj: StartUpSequenceResult) => {
  if (
    returnObj.context.refreshResult.loginRefreshStatus.status ===
    enums.RefreshStatus.failed
  ) {
    returnObj.state = enums.AppStates.FAILED;
    throw new Error('Login refresh failed');
  }

  if (
    returnObj.context.refreshResult.loginRefreshStatus.status ===
    enums.RefreshStatus.notRegisterable
  ) {
    returnObj.state = enums.AppStates.NOT_REGISTERABLE;
    returnObj.context.personData = {};
    returnObj.context.kentico.callCenter = await refreshCallCenter('-1');
    throw new Error('Login refresh not registerable');
  }
};

const initializePersonData = async (returnObj: StartUpSequenceResult) => {
  returnObj.context.personData = await getPersonData();
  returnObj.context.kentico.callCenter = await refreshCallCenter(
    returnObj.context.personData?.person?.companyCode,
  );

  returnObj.context.ERContent = await getEmployerContentSafely(
    returnObj.context.personData?.person?.companyCode,
  );

  try {
    if (returnObj.context.personData.person) {
      returnObj.context.personData.person.preferences = await getPreferences();
    }
  } catch (error) {
    log.error('Error fetching communication preferences during startup', error);
  }
};

const setEmployerPreferences = async (returnObj: StartUpSequenceResult) => {
  try {
    returnObj.context.employerPreferences = await getEmployerPreferences();
  } catch (error) {
    log.error(error);
  }
};

const finalCheck = (returnObj: StartUpSequenceResult) => {
  if (
    returnObj.context.refreshResult.loginRefreshStatus.status ===
    enums.RefreshStatus.failedWithData
  ) {
    returnObj.state = enums.AppStates.FAILED_WITH_DATA;
  } else {
    returnObj.state = enums.AppStates.COMPLETE;
  }
};

/**
 * Attempts to populate the startup context from cached person data so the
 * blocking refresh can be skipped. Each dependent call is resolved
 * independently so a single failure (e.g. employer preferences) doesn't
 * discard the other successful results.
 * @param returnObj The startup sequence result to populate.
 * @returns {Promise<boolean>} True if cached person data was used.
 */
const tryUseCachedData = async (
  returnObj: StartUpSequenceResult,
): Promise<boolean> => {
  try {
    const cachedPersonData = await getPersonData();
    if (
      !cachedPersonData?.person?.companyCode ||
      !cachedPersonData?.person?.customerNumber
    ) {
      return false; // should have at least these 2 values
    }

    returnObj.context.personData = cachedPersonData;

    const [callCenterResult, erContentResult, employerPreferencesResult] =
      await Promise.allSettled([
        refreshCallCenter(cachedPersonData.person.companyCode),
        getEmployerContentSafely(cachedPersonData.person.companyCode),
        getEmployerPreferences(),
      ]);

    if (callCenterResult.status === 'fulfilled') {
      returnObj.context.kentico.callCenter = callCenterResult.value;
    } else {
      log.error(callCenterResult.reason);
    }

    if (erContentResult.status === 'fulfilled') {
      returnObj.context.ERContent = erContentResult.value;
    } else {
      log.error(erContentResult.reason);
    }

    if (employerPreferencesResult.status === 'fulfilled') {
      returnObj.context.employerPreferences = employerPreferencesResult.value;
    } else {
      log.error(employerPreferencesResult.reason);
    }

    // Fire off both login + full refresh non-blocking
    startRefresh(true, true);

    returnObj.context.usingCachedData = true;
    returnObj.context.refreshResult.loginRefreshStatus.status =
      enums.RefreshStatus.started;
    returnObj.context.refreshResult.fullRefreshStatus.status =
      enums.RefreshStatus.started;
    returnObj.state = enums.AppStates.COMPLETE;
    return true;
  } catch (error) {
    log.debug(
      'No cached person data available, falling back to blocking refresh',
      error,
    );
    return false;
  }
};

/**
 * Executes the startup sequence and returns an object that indicates the state
 * along with any contextual data related to that state
 * @returns {{state:string, context:StartupContext}}
 */
export const startUpSequence = async () => {
  const returnObj: StartUpSequenceResult = {
    state: 'INIT',
    context: {
      refreshResult: {
        status: '',
        loginRefreshStatus: { status: enums.RefreshStatus.started },
        fullRefreshStatus: { status: enums.RefreshStatus.started },
        statusDetail: {},
      },
      kentico: {} as any,
    },
  };

  try {
    await checkSplashConfig(returnObj);
    await getKenticoData(returnObj);

    if (returnObj.context?.splashConfig?.showSplash && !isSplashBypassed()) {
      returnObj.state = enums.AppStates.SHOW_SPLASH;
      return returnObj;
    }

    // after confirming splash is not relevant, but before login redirects, store the entry point
    if (!sessionStorage.getItem(enums.CacheKeys.entryPoint)) {
      sessionStorage.setItem(
        enums.CacheKeys.entryPoint,
        window.location.pathname + window.location.search,
      );
    }

    await tryAuthenticating(returnObj);

    // Fast path: try to use cached person data to skip the blocking refresh
    const usedCachedData = await tryUseCachedData(returnObj);

    if (!usedCachedData) {
      await loginRefresh(returnObj);
      returnObj.state = enums.AppStates.AUTHENTICATED;
      await checkRefreshStatus(returnObj);
      await initializePersonData(returnObj);
      await setEmployerPreferences(returnObj);
      finalCheck(returnObj);
    }

    // Initialize Amazon Connect chat with user information
    await initializeCommonChat(
      returnObj.context.personData?.person?.firstName,
      returnObj.context.personData?.person?.lastName,
    );
  } catch (error) {
    log.error(error);
  }

  return returnObj;
};
